import { useEffect, useState } from 'react';
import {
  Grid,
  Divider,
  Button,
  useTheme,
  Box,
  CircularProgress,
} from '@mui/material';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import NgSampleModal from './NgSampleModal';
import { GetNgSample } from '../../../services/apiTypes';
import { MANUAL_CONTAINER_NAME } from '../../../_constants/inspectionConst';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../stores/store';
import { useTranslation } from 'react-i18next';
import {
  HEADER_HEIGHT,
  LOADING_BACKDROP_STYLE,
} from '../../../_constants/styleConst';
import { addManual } from '../../../stores/manual/slice';
import { getBlobData } from '../../../services/api/BlobData';
import { isGetBlobData } from '../../../_utils/typeGuardUtils';
import useErrorModal from '../../../hooks/useErrorModal';
import ErrorModal from '../../../_common/Modal/ErrorModal';
import { CustomError } from '../../../services/CustomError';
import { API_CODE } from '../../../_constants/apiConst';
import { ACCEPT_TYPE_MANUAL } from '../../../_constants/manageConst';

type InspectionManualProps = {
  ngSampleList: GetNgSample[];
  manualPath: string;
};

const InspectionManual: React.FC<InspectionManualProps> = ({
  ngSampleList,
  manualPath,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { typeId } = useSelector((state: RootState) => state.selectIds);
  const { manuals } = useSelector((state: RootState) => state.manual);
  const { error, showApiError, showTypeError, hideError } = useErrorModal();
  const [isLoading, setIsLoading] = useState(false);
  // 限度見本のダイアログ
  const [isOpen, setIsOpen] = useState(false);

  const buttonStyles = {
    fontSize: theme.typography.caption.fontSize,
    cursor: 'pointer',
    color: '#000000',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    background: 'none',
    border: 'none',
    padding: 0,
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!manuals[typeId] && manualPath !== '') {
          setIsLoading(true);
          const response = await getBlobData({
            containerName: MANUAL_CONTAINER_NAME,
            path: getManualFileName(),
          });

          if (isGetBlobData(response)) {
            for (const item of response) {
              // システムからアップロードしたPDFファイルをダウンロードすると不明なファイルになるため
              // PDFとして扱う
              const pdfData = item.data.replace(
                'application/octet-stream',
                ACCEPT_TYPE_MANUAL,
              );

              // Base64Stringをそのまま使用するとブラウザの許容サイズを超えるためURLに変換
              const manualUrl = await fetch(pdfData)
                .then((res) => res.blob())
                .then(URL.createObjectURL)
                .then((ret) => {
                  return ret;
                });
              dispatch(
                addManual({
                  typeId: typeId,
                  manual: manualUrl + getDefaultPage(),
                }),
              );
            }
          } else {
            showTypeError(API_CODE.BLOB_DATA);
          }
        }
      } catch (error) {
        if (error instanceof CustomError) {
          error.data = [t('LBL_MANUAL')];
          showApiError(error, API_CODE.BLOB_DATA);
        } else {
          showApiError(error, API_CODE.BLOB_DATA);
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [typeId]);

  const onClickManualButton = () => {
    if (manuals[typeId]) {
      window.open(manuals[typeId], '_blank', 'noopener,noreferrer');
    }
  };

  /** ページ指定部分を除いてファイル名のみ取得 */
  const getManualFileName = () => {
    const hashIndex = manualPath.lastIndexOf('#page=');
    if (hashIndex === -1) {
      // 「#」が見つからない場合は元の文字列を返す
      return manualPath;
    }
    return manualPath.substring(0, hashIndex);
  };
  /**
   * マニュアルの初期表示ページを取得
   * 末尾にサイドバー非表示の「navpanes=0」を追加する
   **/
  const getDefaultPage = () => {
    const hashIndex = manualPath.lastIndexOf('#page=');
    if (hashIndex === -1) {
      // 「#」が見つからない場合は指定なし
      return '#navpanes=0';
    }
    return manualPath.substring(hashIndex) + '&navpanes=0';
  };

  return (
    <>
      <Grid container direction="row-reverse" columnSpacing={2}>
        <Grid size={0.5}></Grid>
        <Grid
          size={11}
          sx={{
            height: '40px',
            textAlign: 'right',
          }}
          container
          direction="row"
          justifyContent="flex-end"
        >
          {ngSampleList.length > 0 && (
            <Button onClick={() => setIsOpen(true)} sx={buttonStyles}>
              {t('BTN_NG_SAMPLE')}
              <ArrowForwardIosSharpIcon
                color="primary"
                fontSize="small"
                sx={{ marginLeft: '5px', verticalAlign: 'middle' }}
              />
            </Button>
          )}
          {manualPath && (
            <Button
              onClick={onClickManualButton}
              sx={{
                ...buttonStyles,
                cursor: !manuals[typeId] ? 'not-allowed' : 'pointer',
                color: !manuals[typeId] ? '#A2A2A2' : '#000000',
              }}
              disabled={!manuals[typeId]}
            >
              {t('BTN_MANUAL')}
              <ArrowForwardIosSharpIcon
                color={!manuals[typeId] ? 'inherit' : 'primary'}
                fontSize="small"
                sx={{ marginLeft: '5px', verticalAlign: 'middle' }}
              />
            </Button>
          )}
        </Grid>
        <Grid size={0.5}></Grid>
        <Divider
          variant="middle"
          sx={{
            width: 'calc(100% - 16px)',
            padding: 0,
            marginRight: 1,
          }}
        />
        <Grid
          size={12}
          sx={{
            textAlign: 'center',
            pl: 1,
            pt: 1,
            height: `calc(100dvh - ${HEADER_HEIGHT} - 155px)`,
          }}
        >
          <Box
            sx={{
              border: '1px solid rgba(0, 0, 0, 0.12)',
              width: 'calc(100% - 8px)',
              height: '100%',
              position: 'relative',
              display: manualPath === '' ? 'none' : 'block',
            }}
          >
            {manuals[typeId] && (
              <embed
                src={manuals[typeId]}
                type={'application/pdf'}
                width="100%"
                height="100%"
              />
            )}
            {isLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <CircularProgress sx={{ ...LOADING_BACKDROP_STYLE }} />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
      {ngSampleList.length > 0 && (
        <NgSampleModal
          isOpen={isOpen}
          ngSampleList={ngSampleList}
          handleClose={() => setIsOpen(false)}
        />
      )}
      {error && <ErrorModal message={error} onClose={hideError} />}
    </>
  );
};

export default InspectionManual;
